import { CloseButton } from '@headlessui/react';
import { FunnelIcon, MagnifyingGlassIcon, XCircleIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { Button } from '@heroui/react';
import { useState } from 'react';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { BadgeButton } from '@/primitives/badge.tsx';
import { Dialog, DialogActions, DialogBody, DialogTitle } from '@/primitives/dialog.tsx';
import { Field, Fieldset } from '@/primitives/fieldset.tsx';
import type {
  SearchCriteriaDisplayDefinition,
  SearchFormProps,
  SearchParams,
} from '../types/search-form.type.ts';

interface SearchCriteria {
  label: string;
  value: any;
  key: any;
}

const SearchForm = <T extends SearchParams>({
  getUrlSearchParams,
  filterForm,
  searchCriteriaDisplayDefinitions,
  getSearchParamModel,
  defaultValue = { keyword: '' },
}: SearchFormProps<T> & {
  searchCriteriaDisplayDefinitions: SearchCriteriaDisplayDefinition[];
  getSearchParamModel: (value: URLSearchParams) => T;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const form = useForm<T>({
    values: getSearchParamModel(searchParams),
    defaultValues: defaultValue,
  });
  const { handleSubmit, watch, setValue } = form;
  const formValues = watch();

  const getSearchCriteria = (searchModel: T) => {
    const thisCriteria: SearchCriteria[] = [];
    for (const prop in searchModel) {
      const definition = searchCriteriaDisplayDefinitions.find(x => x.key === prop);
      const value = definition.displayFunc(searchModel[prop]);
      if (value) {
        thisCriteria.push({ label: definition.label, value: value, key: prop });
      }
    }
    return thisCriteria;
  };

  const onFilterChange = (value: string) => {
    setSearchParams(
      getUrlSearchParams({
        ...formValues,
        keyword: value,
      }),
    );
  };

  const onSubmitForm: SubmitHandler<T> = (value: T) => {
    setSearchParams(
      getUrlSearchParams({
        ...value,
      }),
    );
    setIsOpen(false);
  };

  const clear = () => {
    for (const defaultValueKey in defaultValue) {
      setValue(defaultValueKey as any, defaultValue[defaultValueKey] as any);
    }
  };

  const remove = (value: SearchCriteria) => {
    setValue(value.key, null);
    handleSubmit(onSubmitForm)();
  };

  return (
    <div className='flex flex-row justify-start gap-10'>
      <Fieldset>
        <Field>
          <div className='mt-2 flex'>
            <div className='-mr-px grid grow grid-cols-1 focus-within:relative'>
              <input
                onInput={(event: any) => onFilterChange(event.target.value)}
                value={formValues?.keyword ?? ''}
                type='text'
                placeholder='Search'
                className='col-start-1 row-start-1 block w-full rounded-l-md bg-white py-1.5 pl-10 pr-3 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-secondary-600 sm:pl-9 sm:text-sm/6'
              />
              <MagnifyingGlassIcon
                aria-hidden='true'
                className='pointer-events-none col-start-1 row-start-1 ml-3 size-5 self-center text-gray-400 sm:size-4'
              />
            </div>
            {filterForm && (
              <button
                type='button'
                onClick={() => setIsOpen(true)}
                className='flex shrink-0 items-center gap-x-1.5 rounded-r-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 hover:bg-gray-50 focus:relative focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-secondary-600'
              >
                <FunnelIcon
                  title='Additional filters'
                  aria-hidden='true'
                  className='-ml-0.5 size-4 text-gray-400'
                />
              </button>
            )}
          </div>
        </Field>
      </Fieldset>
      <div className='flex items-center gap-5'>
        {getSearchCriteria(formValues).map(value => (
          <BadgeButton key={value.key} onClick={() => remove(value)}>
            {value.label}: {value.value}
            <XCircleIcon className='size-4 text-gray-400' />
          </BadgeButton>
        ))}
      </div>

      {filterForm && isOpen && (
        <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
          <FormProvider {...form}>
            <form onSubmit={handleSubmit(onSubmitForm)}>
              <div className='flex flex-row justify-between'>
                <DialogTitle>Filter</DialogTitle>
                <CloseButton as={Button} variant='light'>
                  <XMarkIcon />
                </CloseButton>
              </div>
              <DialogBody>{filterForm}</DialogBody>
              <DialogActions>
                <Button
                  type='button'
                  variant='bordered'
                  onClick={() => {
                    clear();
                  }}
                >
                  Clear
                </Button>
                <Button type='submit' color='primary' variant='solid'>
                  Apply
                </Button>
              </DialogActions>
            </form>
          </FormProvider>
        </Dialog>
      )}
    </div>
  );
};

export default SearchForm;
